import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {Router} from '@angular/router';
import {Observable, of, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {environment} from '../../environments/environment';
import {NGXLogger} from 'ngx-logger';

// based on https://stackoverflow.com/questions/46017245/how-to-handle-unauthorized-requestsstatus-with-401-or-403-with-new-httpclient
@Injectable({
  providedIn: 'root'
})
export class AuthInterceptorService implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    private router: Router,
    private logger: NGXLogger,
  ) { }

  // AuthService handles expected anonymous responses from these endpoints.
  private static readonly AUTO_SIGNOUT_EXEMPT_PATH_SUFFIXES = [
    '/bridge/vault_auth_session',
    '/bridge/vault_auth_signout',
  ];

  // Discovery requests must work before the cookie mode is known.
  private static readonly PUBLIC_CREDENTIALLESS_PATH_SUFFIXES = [
    '/bridge/config',
    '/bridge/org',
  ];

  private handleAuthError(err: HttpErrorResponse): Observable<any> {
    //handle your auth error or rethrow
    if (err.status === 401 || err.status === 403) {
      //navigate /delete cookies or whatever
      this.authService.Signout()
      this.router.navigateByUrl('/auth/signin');
      // if you've caught / handled the error, you don't want to rethrow it unless you also want downstream consumers to have to handle it as well.
      return of(err.message); // or EMPTY may be appropriate here
    }
    return throwError(err);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    this.logger.info("Intercepting Request", req)

    //only intercept requests to the fasten API & lighthouse, all other requests should be sent as-is
    let reqUrl = new URL(req.url)
    let lighthouseUrl = new URL(environment.lighthouse_api_endpoint_base)
    let apiUrl = new URL(environment.connect_api_endpoint_base)

    if(
      !((reqUrl.origin == apiUrl.origin && reqUrl.pathname.startsWith(apiUrl.pathname)) ||
        (reqUrl.origin == lighthouseUrl.origin && reqUrl.pathname.startsWith(lighthouseUrl.pathname)))
    ){
      return next.handle(req)
    }

    const isPublicCredentiallessRequest = AuthInterceptorService.PUBLIC_CREDENTIALLESS_PATH_SUFFIXES
      .some(pathSuffix => reqUrl.pathname.endsWith(pathSuffix))
    if (isPublicCredentiallessRequest) {
      return next.handle(req.clone({withCredentials: false}))
    }

    // Remaining API requests may use a legacy or HttpOnly cookie.
    const authReq = req.clone({withCredentials: true});

    const isExemptFromAutoSignout = AuthInterceptorService.AUTO_SIGNOUT_EXEMPT_PATH_SUFFIXES
      .some(pathSuffix => reqUrl.pathname.endsWith(pathSuffix))
    if (isExemptFromAutoSignout) {
      return next.handle(authReq)
    }

    // catch the error, make specific functions for catching specific errors and you can chain through them with more catch operators
    return next.handle(authReq).pipe(catchError(x=> this.handleAuthError(x))); //here use an arrow function, otherwise you may get "Cannot read property 'navigate' of undefined" on angular 4.4.2/net core 2/webpack 2.70
  }
}
